// src/components/VencimientoSuscripcion.tsx
//
// Avisa cuándo vence la suscripción, en el panel.
//
// POR QUÉ EXISTE
// --------------
// El dato vivía solo en "Mi suscripción", y a esa pantalla se llega
// únicamente desde el menú del Header: hay que ir a buscar algo que no sabés
// que existe. Y el único aviso automático aparecía cuando faltaba UN día.
//
// Florencia paga por transferencia para ahorrarnos la comisión de Mercado
// Pago y los 24 días que retiene el dinero, así que tiene que acordarse,
// entrar al banco y hacerla. Nos dijo que no sabía cuándo le vencía y pagaba
// cuando se acordaba: 2-jun, 20-jun, 12-jul, 20-ago, 7-sep.
//
// CUÁNDO APARECE, Y POR QUÉ NO SIEMPRE
// ------------------------------------
// Solo en los últimos DIAS_AVISO días. Al principio esto se mostraba siempre,
// y con un vencimiento en 2027 eso es un renglón que se mira todos los días
// para no decir nada — y un cartel que está siempre se vuelve invisible justo
// cuando tiene algo que decir. Para consultar la fecha en cualquier momento
// está "Mi suscripción", que es su lugar.
//
// SE PUEDE CERRAR, PERO NO PARA SIEMPRE
// -------------------------------------
// La X recuerda el descarte ATADO A LA FECHA DE VENCIMIENTO. Así, cerrarlo
// este mes no silencia el aviso del mes que viene: cuando el vencimiento
// cambia, la llave cambia y el aviso vuelve.
//
// Y el último día no se puede ocultar. Es el único que ya no admite "después
// lo veo".

import React, { useState } from 'react';
import { CalendarCheck, AlertTriangle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';

/** Desde cuántos días antes se avisa. Alineado con el backend. */
const DIAS_AVISO = 7;

const llave = (fecha: Date) =>
  `consultar:vencimiento_oculto:${fecha.toISOString().slice(0, 10)}`;

const VencimientoSuscripcion: React.FC = () => {
  const navigate = useNavigate();
  const { expiresAt, daysRemaining, isExpired, isTrial, loading } = useSubscription();
  const [oculto, setOculto] = useState(false);

  // Sin fecha no se inventa nada: hay cuentas de cortesía sin vencimiento.
  // La prueba gratis tampoco: tiene su propio aviso en los últimos días
  // (SubscriptionBanner). Con los dos, una cuenta en prueba veía "Tu
  // suscripción vence..." una semana antes y otro cartel repitiéndolo.
  if (loading || isExpired || isTrial || !expiresAt) return null;

  const dias = daysRemaining;
  if (dias > DIAS_AVISO) return null;

  const urgente = dias <= 1;

  // El descarte vale para este vencimiento, no para siempre. Y el último día
  // no se puede tapar.
  let descartado = oculto;
  try {
    if (!urgente && localStorage.getItem(llave(expiresAt)) === '1') descartado = true;
  } catch {
    /* modo privado: que no se pueda recordar el descarte no es grave */
  }
  if (descartado) return null;

  const fecha = expiresAt.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' });

  const tono = urgente
    ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-amber-50 border-amber-200 text-amber-900';

  return (
    <div className={`w-full flex items-center gap-2 border rounded-xl px-4 py-2.5 text-[13px] ${tono}`}>
      {urgente ? (
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      ) : (
        <CalendarCheck className="w-4 h-4 flex-shrink-0" />
      )}

      <span className="flex-1 min-w-0">
        {dias === 0 ? (
          <>Tu suscripción <strong>vence hoy</strong></>
        ) : dias === 1 ? (
          <>Tu suscripción <strong>vence mañana</strong></>
        ) : (
          <>
            Tu suscripción vence el <strong>{fecha}</strong> — te quedan {dias} días
          </>
        )}
      </span>

      <button
        onClick={() => navigate('/professional/subscription')}
        className="flex-shrink-0 font-semibold underline underline-offset-2"
      >
        Ver detalle
      </button>

      {/* El último día no se puede cerrar: es el que ya no admite "después". */}
      {!urgente && (
        <button
          onClick={() => {
            setOculto(true);
            try {
              localStorage.setItem(llave(expiresAt), '1');
            } catch {
              /* ver arriba */
            }
          }}
          aria-label="Cerrar aviso"
          title="Cerrar"
          className="flex-shrink-0 p-1 -mr-1 rounded hover:bg-black/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default VencimientoSuscripcion;
